// Shared shape and helpers for application cards (users/{uid}/emails docs),
// used by the Tracker and the read-only archive view.

import type { StageEvent } from "./applicationHistory";

// One synced email on a card (backend: main._card_email_entry).
export type CardEmail = {
  message_id: string;
  subject: string;
  date: string; // ISO, when Gmail received it
  label: string;
  score: number;
  moved: boolean; // whether this email changed the card's status
  /** "oa_completed": an "assessment completed" email; it marks the OA done but doesn't move the card */
  kind?: "oa_completed";
};

// An OA / interview deadline (backend: deadlines.deadline_to_card). For an
// interview, source "email" is the date to book a slot by.
export type CardDeadline = {
  type: "oa" | "interview";
  at: string; // ISO (UTC)
  allDay: boolean;
  source: "manual" | "email" | "invite";
  messageId?: string;
  phrase?: string;
};

export type ApiItem = {
  id: string;
  company: string;
  date: string;
  predicted_label: string;
  role?: string;
  szn?: string;
  lctn?: string;
  notes?: string;
  history?: StageEvent[];
  // Email trail (set by Gmail sync; empty for manually added cards)
  emails?: CardEmail[];
  body?: string;
  messageIds?: string[];
  threadIds?: string[];
  manual?: boolean;
  deadline?: CardDeadline | null;
  /** ISO; an "assessment completed" email said the OA was finished (backend: oa_completion.py) */
  oaCompletedAt?: string;
};

export type Status = "submitted" | "interview" | "oa" | "rejected" | "offer" | "ghosted";

const LABEL_MAP: Record<string, Status> = {
  applied: "submitted",
  submitted: "submitted",
  "application received": "submitted",
  interview: "interview",
  oa: "oa",
  "online assessment": "oa",
  rejected: "rejected",
  offer: "offer",
  ghosted: "ghosted",
};

export function toStatus(label?: string): Status | null {
  if (!label) return null;
  return LABEL_MAP[label.toLowerCase()] ?? null;
}

/**
 * Whether a card is a job application. Syncs before Nov 2025 also made cards
 * for non-job emails ("Not applicable"); they're never shown or counted.
 */
export const isApplication = (item: ApiItem) => toStatus(item.predicted_label) !== null;

/** Firestore card docs -> the applications among them (see isApplication). */
export const toApplications = (docs: { id: string; data: () => Record<string, any> }[]): ApiItem[] =>
  docs.map((d) => toApiItem(d.id, d.data())).filter(isApplication);

// Firestore card doc -> the item the board / rows / Sankey render.
export function toApiItem(id: string, data: Record<string, any>): ApiItem {
  return {
    id,
    company: data.company || "Unknown",
    date: data.date || "",
    predicted_label: data.label || "submitted",
    role: data.role || "",
    szn: data.szn || "",
    lctn: data.lctn || "",
    notes: data.notes || "",
    history: Array.isArray(data.history) ? (data.history as StageEvent[]) : [],
    emails: Array.isArray(data.emails) ? (data.emails as CardEmail[]) : [],
    body: typeof data.body === "string" ? data.body : "",
    // Older cards predate messageIds; their one email is message_id
    messageIds: Array.isArray(data.messageIds)
      ? data.messageIds
      : data.message_id && !data.manual_entry
      ? [data.message_id]
      : [],
    threadIds: Array.isArray(data.threadIds) ? data.threadIds : [],
    manual: Boolean(data.manual_entry),
    deadline: data.deadline && typeof data.deadline.at === "string" ? (data.deadline as CardDeadline) : null,
    ...(typeof data.oaCompletedAt === "string" ? { oaCompletedAt: data.oaCompletedAt } : {}),
  };
}

export function matchesQuery(item: ApiItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const has = (s?: string) => (s || "").toLowerCase().includes(q);
  return (
    has(item.company) ||
    has(item.role) ||
    has(item.szn) ||
    has(item.lctn) ||
    has(item.notes) ||
    has(item.predicted_label) ||
    has(item.date)
  );
}
